import { ArrowLeft, Bell, Download, Plus, Search, Settings } from 'lucide-react';
import React from 'react';

export interface PageHeaderProps {
  title: string;
  description?: string;
  addLabel?: string;
  exportLabel?: string;
  onAdd?: () => void;
  onExport?: () => void;
  onSearch?: () => void;
  onBell?: () => void;
  onSettings?: () => void;
  showAdd?: boolean;
  showExport?: boolean;
  showSearch?: boolean;
  showBell?: boolean;
  showSettings?: boolean;
  showActions?: boolean;
  extraLabel?: string;
  onExtra?: () => void;
  showExtra?: boolean;
  permissions?: Record<string, boolean>;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  addLabel,
  exportLabel,
  onAdd,
  onExport,
  onSearch,
  onBell,
  onSettings,
  extraLabel,
  onExtra,
  showExtra = false,
  showAdd = true,
  showExport = true,
  showSearch = false,
  showBell = false,
  showSettings = false,
  showActions = true,
  permissions,
}) => {
  const isCreateAction = /(Add|Create|Upload)/i.test(addLabel ?? '');

  const label = addLabel?.toLowerCase() ?? '';
  const isReturnAction = label.includes('return');
  const canCreate = !isCreateAction || permissions?.create;

  return (
    <header className="sticky top-0 z-40 border-b shadow-lg shadow-[#87074C40] border-[#421C43] bg-[#F2EBEE] px-5 py-4 flex items-center justify-between">
      {/* Left Section */}
      <div>
        <h2 className="text-xl font-bold text-[#421C43] capitalize">{title}</h2>
        {description && <p className="text-base font-medium text-[#621C43]">{description}</p>}
      </div>

      {/* Dashboard Icons */}
      <div className="flex text-sm text-[#4A082D] gap-4 px-2 py-2 rounded-md">
        {showSearch && onSearch && (
          <button onClick={onSearch}>
            <Search className="h-4 w-4" />
          </button>
        )}
        {showBell && onBell && (
          <button onClick={onBell}>
            <Bell className="h-4 w-4" />
          </button>
        )}
        {showSettings && onSettings && (
          <button onClick={onSettings}>
            <Settings className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Right Section - Buttons */}
      {showActions && (
        <div className="flex gap-3">
          {showExtra && onExtra && (
            <button
              onClick={onExtra}
              className="flex items-center gap-1.5 text-sm bg-[#FFF8ED4D] text-[#4A082D] px-4 py-4 rounded-lg hover:bg-[#FFF8ED] transition"
            >
              <ArrowLeft className="h-5 w-5" />
              <p className="font-medium text-base">{extraLabel || 'Back'}</p>
            </button>
          )}

          {showAdd && onAdd && (
            <button
              onClick={onAdd}
              disabled={!canCreate}
              className={`
      flex items-center gap-1.5 text-sm
      bg-[#FFF8ED4D] text-[#4A082D]
      px-4 py-4 rounded-lg transition
      ${!canCreate ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[#FFF8ED]'}
    `}
            >
              {isReturnAction ? <ArrowLeft className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              <p className="font-medium text-base">{addLabel || 'Add'}</p>
            </button>
          )}

          {showExport && onExport && (
            <button
              onClick={onExport}
              disabled={!permissions?.export}
              className={`flex items-center gap-1.5 text-sm bg-[#FFF8ED4D] text-[#4A082D] px-4 py-4 rounded-lg hover:bg-[#FFF8ED] transition 
                ${!permissions?.export ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[#FFF8ED]'}`}
            >
              <Download className="h-5 w-5" />
              <p className="font-medium text-base"> {exportLabel || 'Export'}</p>
            </button>
          )}
        </div>
      )}
    </header>
  );
};

export default PageHeader;
